import type { RecentSaleRow } from '@mentora/data-access'
import type { PriceFormatter } from '../price-formatter'
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { getFormatter, getTranslations } from 'next-intl/server'

const statusVariants: Record<
  RecentSaleRow['status'],
  'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive'
> = {
  completed: 'success',
  refunded: 'warning',
  pending: 'secondary',
  failed: 'destructive',
  abandoned: 'outline',
  cancelled: 'outline',
}

/** Latest course enrollments and, when the scope includes them, event tickets. */
export async function RecentSalesTable({
  rows,
  formatPrice,
}: {
  rows: RecentSaleRow[]
  formatPrice: PriceFormatter
}) {
  const t = await getTranslations('dashboard.recent')
  const tStatus = await getTranslations('dashboard.paymentStatus')
  const format = await getFormatter()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0
          ? (
              <p className="px-6 py-10 text-center text-sm text-muted-foreground">{t('empty')}</p>
            )
          : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">{t('columns.buyer')}</th>
                      <th className="px-4 py-3 font-medium">{t('columns.course')}</th>
                      <th className="px-4 py-3 font-medium">{t('columns.status')}</th>
                      <th className="px-4 py-3 text-right font-medium">{t('columns.amount')}</th>
                      <th className="px-4 py-3 text-right font-medium">{t('columns.date')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr
                        key={row.id}
                        className="border-b border-border/40 last:border-b-0"
                      >
                        <td className="px-4 py-3 text-foreground">{row.buyerName ?? '—'}</td>
                        <td className="px-4 py-3">
                          <p className="text-foreground">{row.itemTitle}</p>
                          <p className="text-xs text-muted-foreground">
                            {row.kind === 'event'
                              ? `${t('kind.ticket')} · /events/${row.itemSlug}`
                              : `${t('kind.course')} · /${row.itemSlug}`}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={statusVariants[row.status]}>
                            {tStatus(row.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                          {formatPrice(row.amountCents)}
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          {format.dateTime(new Date(row.createdAt), { dateStyle: 'short' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </CardContent>
    </Card>
  )
}
